import { Box, Card, CardContent, CardHeader, Typography, Stack, Chip } from "@mui/material";
import { DateField, EditButton, RecordContextProvider, useListContext, RaRecord, EmailField } from "react-admin";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { StatusChipField } from "@/components/common/fields/StatusChipField";
import { getProps } from "@/utils/helpers/columnsHelper";
import { AvatarField } from "@/components/common/fields/AvatarField";
import { useNavigate } from "react-router";
import { ResourceName } from "@/types";

const ContactDetail = ({
  icon,
  children,
  color = "text.secondary",
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  color?: string;
}) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Box sx={{ color, display: "flex", alignItems: "center", minWidth: 20 }}>{icon}</Box>
    <Typography variant="body2" color={color} noWrap sx={{ flex: 1 }}>
      {children}
    </Typography>
  </Stack>
);

const MobileGrid = () => {
  const { data, error, isPending } = useListContext<RaRecord>();
  const navigate = useNavigate();

  if (error || isPending || data.length === 0) {
    return null;
  }

  return (
    <Box margin="0.5em">
      {data.map((record) => (
        <RecordContextProvider key={record.id} value={record}>
          <Card
            sx={{
              margin: "0.5rem 0",
              transition: "all 0.2s ease-in-out",
              border: "1px solid",
              borderColor: "divider",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 3,
                borderColor: "primary.light",
              },
            }}
            onClick={() => {
              navigate(`/clients/${record.id}/show`);
            }}
          >
            <CardHeader
              title={
                <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
                  {record.client_legal_name || record.client_short_name}
                </Typography>
              }
              subheader={
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                  <StatusChipField {...getProps("client_status", ResourceName.CLIENTS)} />
                  {record.has_active_policy && (
                    <Chip
                      label="Active Policy"
                      size="small"
                      color="success"
                      variant="filled"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Stack>
              }
              avatar={
                <Box
                  sx={{
                    "& .MuiStack-root": {
                      "& .MuiTypography-root": { display: "none" },
                    },
                  }}
                >
                  <AvatarField nameSource="client_legal_name" size={45} />
                </Box>
              }
              action={<EditButton />}
            />
            <CardContent sx={{ pt: 0 }}>
              {/* Contact Information */}
              <Stack spacing={1.5}>
                {record.client_contact_name && (
                  <ContactDetail icon={<BusinessIcon fontSize="small" />}>{record.client_contact_name}</ContactDetail>
                )}

                {record.client_email && (
                  <ContactDetail icon={<EmailIcon fontSize="small" />}>
                    <EmailField source="client_email" />
                  </ContactDetail>
                )}

                {record.client_phone_no && (
                  <ContactDetail icon={<PhoneIcon fontSize="small" />}>{record.client_phone_no}</ContactDetail>
                )}

                {record.client_state_headquartered && (
                  <ContactDetail icon={<LocationOnIcon fontSize="small" />}>
                    {record.client_state_headquartered}
                  </ContactDetail>
                )}
              </Stack>

              {/* Footer with additional info */}
              <Box
                sx={{
                  mt: 2,
                  pt: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                {record.modified_datetime && (
                  <Typography variant="caption" color="text.secondary">
                    Updated <DateField source="modified_datetime" showTime={false} />
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </RecordContextProvider>
      ))}
    </Box>
  );
};

export default MobileGrid;
